/**
 * Breadcrumbs, progressive enhancement (snippets/bettino-breadcrumbs.liquid).
 *
 * The server-rendered trail is already correct. This only adds:
 *   1. Remembering the collection a shopper browsed (sessionStorage), and on a
 *      product page swapping the middle crumb to it when the product belongs to
 *      it and the URL carried no collection of its own.
 *   2. A soft fade on the right edge while the crumbs overflow and scroll.
 * The JSON-LD is never touched. Any failure leaves the printed trail as it is.
 */

const STORAGE_KEY = 'bettino-last-collection';

function readStored() {
  try {
    const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    return stored && stored.handle && stored.title && stored.url ? stored : null;
  } catch {
    return null;
  }
}

function writeStored(value) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: the server trail stands.
  }
}

function swapMiddleCrumb(nav) {
  const crumb = nav.querySelector('[data-crumb="collection"]');
  const link = crumb && crumb.querySelector('a');
  const stored = readStored();
  if (!link || !stored || nav.dataset.trail === 'url') return;

  const belongsTo = (nav.dataset.productCollections || '').split(' ');
  // The skip list comes from Theme settings, so a catch-all collection that was
  // browsed never replaces a real one.
  const skipped = (nav.dataset.skip || '').split(' ');
  if (skipped.includes(stored.handle)) return;
  // Same-site paths only, so a tampered value cannot point elsewhere.
  if (!belongsTo.includes(stored.handle) || !stored.url.startsWith('/') || stored.url.startsWith('//')) return;

  link.textContent = stored.title;
  link.setAttribute('href', stored.url);
}

function watchOverflow(nav) {
  const list = nav.querySelector('ol');
  if (!list) return;

  const update = () => {
    const hidden = list.scrollWidth - list.clientWidth - list.scrollLeft;
    list.toggleAttribute('data-overflow-end', hidden > 1);
  };

  list.addEventListener('scroll', update, { passive: true });
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(update);
    observer.observe(list);
    for (const item of list.children) observer.observe(item);
  }
  update();
}

function init(nav) {
  if (nav.hasAttribute('data-ready')) return;
  nav.setAttribute('data-ready', '');

  if (nav.dataset.page === 'collection') {
    writeStored({
      handle: nav.dataset.collectionHandle,
      title: nav.dataset.collectionTitle,
      url: nav.dataset.collectionUrl,
    });
  } else {
    swapMiddleCrumb(nav);
  }

  watchOverflow(nav);
}

function initAll() {
  document.querySelectorAll('[data-bettino-breadcrumbs]').forEach(init);
}

initAll();
document.addEventListener('shopify:section:load', initAll);
